import { useCallback, useEffect, useState } from "react";

import { LocalStorageKeys, ROUTES, SOCKET_MESSAGE_TYPE } from "@is/constants";
import { useDeviceIsActive } from "@is/hooks";
import { useSessionSegments } from "@is/hooks/useSessionSegments";
import { useStoreEvents } from "@is/hooks/useStoreEvents";
import { LocalStorageService } from "@is/services";
import { TrackingEventTypes, trackEvent } from "@is/services/EventTracking";

import { useSession } from "./useSession";
import { useWebSocketWorkerMessage } from "./websocket";

function useDeviceStatus() {
  const deviceId = LocalStorageService.getItem(LocalStorageKeys.DeviceId);
  const storeId = LocalStorageService.getItem(LocalStorageKeys.StoreId);
  const previousSessionId = LocalStorageService.getItem(
    LocalStorageKeys.SessionId
  );

  const [shouldFetchSessionSegments, setShouldFetchSessionSegments] =
    useState<boolean>(false);

  const { resetSession } = useSession();

  const { storeEvent } = useStoreEvents();

  const { data } = useDeviceIsActive({ deviceId, storeId });
  const { data: sessionSegments } = useSessionSegments(
    shouldFetchSessionSegments
  );

  const unregisterDevice = useCallback(() => {
    LocalStorageService.removeItem(LocalStorageKeys.DeviceId);
    LocalStorageService.removeItem(LocalStorageKeys.StoreId);
    LocalStorageService.removeItem(LocalStorageKeys.SessionId);
    LocalStorageService.removeItem(LocalStorageKeys.SearchId);

    const startUrl = LocalStorageService.getItem(LocalStorageKeys.StartUrl);

    if (startUrl) {
      window.location.href = startUrl;
    } else {
      const tenantId = LocalStorageService.getItem(LocalStorageKeys.TenantId);

      window.location.href = `/instore?t=${tenantId}`;
    }
  }, []);

  useEffect(() => {
    if (data && typeof data.isActive === "boolean" && data.isActive === false) {
      storeEvent({
        eventType: TrackingEventTypes.StoreArchived,
        data: {},
        skipSessionId: true,
        deviceId,
        storeId,
        createdAt: new Date().toISOString(),
      });

      unregisterDevice();
      terminateWorker();
    }
  }, [data]);

  useEffect(() => {
    if (data && typeof data.isActive === "boolean" && data.isActive === true) {
      setShouldFetchSessionSegments(true);
    }
  }, [data]);

  useEffect(() => {
    if (sessionSegments) {
      trackEvent({
        eventType: TrackingEventTypes.SessionSegments,
        data: {
          segmentIds: sessionSegments,
        },
      });

      setShouldFetchSessionSegments(false);
    }
  }, [sessionSegments]);

  const { terminateWorker } = useWebSocketWorkerMessage(
    (event: MessageEvent) => {
      switch (event.data.type) {
        case SOCKET_MESSAGE_TYPE.STORE_ARCHIVED: {
          const { storeId } = event.data;

          if (
            storeId === LocalStorageService.getItem(LocalStorageKeys.StoreId)
          ) {
            storeEvent({
              eventType: TrackingEventTypes.StoreArchived,
              data: {},
              skipSessionId: true,
              deviceId,
              storeId,
              createdAt: new Date().toISOString(),
            });

            unregisterDevice();
            terminateWorker();
          }
          break;
        }
        case SOCKET_MESSAGE_TYPE.DEVICE_ARCHIVED: {
          const { deviceId, storeId } = event.data;

          if (
            deviceId ===
              LocalStorageService.getItem(LocalStorageKeys.DeviceId) &&
            storeId === LocalStorageService.getItem(LocalStorageKeys.StoreId)
          ) {
            storeEvent({
              eventType: TrackingEventTypes.DeviceArchived,
              data: {},
              skipSessionId: true,
              deviceId,
              storeId,
              createdAt: new Date().toISOString(),
            });

            unregisterDevice();
            terminateWorker();
          }
          break;
        }
        case SOCKET_MESSAGE_TYPE.RELEASE_NOTIFICATION: {
          const { version } = event.data;

          storeEvent({
            eventType: TrackingEventTypes.ReleaseNotification,
            data: { version },
            skipSessionId: true,
            deviceId,
            storeId,
            createdAt: new Date().toISOString(),
          });

          break;
        }
        case SOCKET_MESSAGE_TYPE.INITIALIZATION_COMPLETED: {
          const {
            version,
            externalStoreId,
            deviceType,
            locationId,
            storeName,
          } = event.data;

          storeEvent({
            eventType: TrackingEventTypes.DeviceMetadata,
            data: {
              externalStoreId,
              deviceType,
              locationId,
              deviceId,
              storeId,
              storeName,
            },
            createdAt: new Date().toISOString(),
            sessionId: previousSessionId,
          });

          const currentAppVersion = LocalStorageService.getItem(
            LocalStorageKeys.Version
          );

          if (version && version !== currentAppVersion) {
            const isEntryView = window.location.pathname.includes(
              ROUTES.EntryView
            );
            LocalStorageService.setItem(LocalStorageKeys.Version, version);
            LocalStorageService.setItem(
              LocalStorageKeys.LastUpdateAt,
              new Date().toISOString()
            );

            storeEvent({
              eventType: TrackingEventTypes.InitializationCompleted,
              data: { version },
              skipSessionId: true,
              deviceId,
              storeId,
              createdAt: new Date().toISOString(),
            });

            if (isEntryView) {
              resetSession();

              window.location.href = `/instore${ROUTES.EntryView}`;
            } else {
              LocalStorageService.setItem(LocalStorageKeys.Reload, true);
            }
          }
          break;
        }
      }
    }
  );
}

export default useDeviceStatus;
